<template>
  <div class="control is-horizontal wide open-pickup-times-control">
    <div class="control-label">
      <label-name labelKey="SITE-PWA-OPEN_AND_PICKUP_TIMES"></label-name>
    </div>

    <div class="control open-pickup-times-container">
      <!-- HEADER: UNIFIED SEGMENTED TABS (7 DAYS + WOCHENÜBERSICHT) -->
      <div class="day-tabs-header">
        <div class="day-tabs-container">
          <div class="day-tabs">
            <button
              v-for="day in weekDaysList"
              :key="day.key"
              type="button"
              class="day-tab-button"
              :class="{
                'is-active': activeDay === day.key && !showAllDaysView,
                'is-closed': !daysData[day.key] || !daysData[day.key].isOpen
              }"
              @click="activeDay = day.key; showAllDaysView = false"
            >
              <span class="day-label">{{ day.label }}</span>
              <span
                v-if="daysData[day.key] && daysData[day.key].isOpen"
                class="tag is-rounded day-badge"
                :class="{
                  'is-active-badge': activeDay === day.key && !showAllDaysView,
                  'is-inactive-badge': activeDay !== day.key || showAllDaysView
                }"
              >
                {{ (daysData[day.key].pickUpThresholds || []).length }}
              </span>
              <span v-else class="tag is-rounded day-badge is-closed-tag">
                &times;
              </span>
            </button>

            <!-- 8TH TAB: WOCHENÜBERSICHT -->
            <div class="day-tab-divider"></div>
            <button
              type="button"
              class="day-tab-button overview-tab-button"
              :class="{ 'is-active': showAllDaysView }"
              @click="showAllDaysView = true"
              title="Wochenübersicht aller Tage anzeigen"
            >
              <span class="material-symbols-outlined tab-icon">calendar_view_week</span>

            </button>
          </div>
        </div>
      </div>

      <!-- FULL WEEK OVERVIEW MODE -->
      <div v-if="showAllDaysView" class="all-days-overview">
        <div
          v-for="day in weekDaysList"
          :key="day.key"
          class="overview-day-card"
          :class="{ 'is-closed-card': !daysData[day.key] || !daysData[day.key].isOpen }"
        >
          <div class="overview-day-header">
            <div class="overview-day-title">
              <strong>{{ day.label }}</strong>
              <span
                class="tag is-small day-status-badge"
                :class="daysData[day.key] && daysData[day.key].isOpen ? 'is-success is-light' : 'is-light'"
              >
                {{ daysData[day.key] && daysData[day.key].isOpen ? 'Geöffnet' : 'Geschlossen' }}
              </span>
            </div>
            <div class="overview-day-hours has-text-grey is-size-7">
              <template v-if="daysData[day.key] && daysData[day.key].isOpen">
                {{ daysData[day.key].opening_time || '—' }} – {{ daysData[day.key].closing_time || '—' }} Uhr
                &bull; {{ (daysData[day.key].pickUpThresholds || []).length }} Abholzeit(en)
              </template>
              <template v-else>
                Keine Öffnungszeiten
              </template>
            </div>
            <button
              type="button"
              class="button is-small is-light ml-auto"
              @click="activeDay = day.key; showAllDaysView = false"
            >
              <span class="material-symbols-outlined">edit</span> Bearbeiten
            </button>
          </div>

          <div
            v-if="daysData[day.key] && daysData[day.key].isOpen && daysData[day.key].pickUpThresholds.length > 0"
            class="overview-thresholds-chips"
          >
            <span
              v-for="(t, idx) in daysData[day.key].pickUpThresholds"
              :key="idx"
              class="tag is-info is-light is-small overview-threshold-chip"
            >
              <template v-if="t.orderBeforeTime && t.pickUpAtTime">
                bis {{ t.orderBeforeTime }} &rarr; ab {{ t.pickUpAtTime }}
                <span v-if="t.pickUpAtNextDay" class="has-text-weight-bold chip-next-day">(+1 Tag)</span>
              </template>
              <template v-else-if="!t.orderBeforeTime && !t.pickUpAtTime">
                Annahmeschluss (keine Abholung)
              </template>
              <template v-else>
                {{ t.orderBeforeTime ? 'bis ' + t.orderBeforeTime : '' }}
                {{ t.pickUpAtTime ? 'ab ' + t.pickUpAtTime : '' }}
              </template>
            </span>
          </div>
        </div>
      </div>

      <!-- SINGLE DAY VIEW (ACTIVE DAY) -->
      <div v-else class="day-detail-card">
        <!-- DAY HEADER & STATUS -->
        <div class="day-card-header">
          <div class="day-title-wrap">
            <h4 class="day-heading mb-0">
              <span class="material-symbols-outlined">calendar_today</span>
              <span>{{ currentDayMeta.label }}</span>
            </h4>
            <span
              class="tag day-status-badge"
              :class="activeDayData.isOpen ? 'is-success' : 'is-light'"
            >
              {{ activeDayData.isOpen ? 'Geöffnet' : 'Geschlossen' }}
            </span>
          </div>

          <div class="day-status-toggle">
            <span class="status-toggle-label">Apotheke geöffnet</span>
            <b-switch
              :key="'day-switch-' + activeDay + '-' + (activeDayData.isOpen ? '1' : '0')"
              v-model="activeDayData.isOpen"
              type="primary"
              size="small"
              @change="onDayOpenToggle"
              @input="onDayOpenToggle"
            ></b-switch>
          </div>
        </div>

        <!-- CLOSED DAY NOTICE -->
        <div v-if="!activeDayData.isOpen" class="day-closed-notice">
          <div class="closed-icon-wrap">
            <span class="material-symbols-outlined">door_front</span>
          </div>
          <div class="closed-text-wrap">
            <p class="has-text-weight-semibold mb-1">Am {{ currentDayMeta.label }} ist die Apotheke geschlossen.</p>
            <p class="has-text-grey is-size-7 mb-2">Es sind keine Öffnungszeiten oder Abholzeiten hinterlegt.</p>
            <button
              type="button"
              class="button is-blue is-outline"
              @click="openCurrentDay"
            >
              <span class="material-symbols-outlined">add</span>
              {{ currentDayMeta.label }} öffnen &amp; Zeiten eintragen
            </button>
          </div>
        </div>

        <!-- OPEN DAY CONTENT -->
        <div v-else class="day-open-content">
          <!-- OPENING HOURS SECTION -->
          <div class="hours-section">
            <div class="hours-row">
              <div class="hours-label">
                <span class="material-symbols-outlined">schedule</span>
                <strong>Öffnungszeit:</strong>
              </div>

              <div class="hours-inputs">
                <span class="hours-prefix">Offen von</span>
                <div class="select hours-select">
                  <select v-model="activeDayData.opening_time" @change="emitUpdate">
                    <option :value="null">-- geschlossen --</option>
                    <option v-for="t in allTimeOptions" :key="'open-' + t.value" :value="t.value">
                      {{ t.name }}
                    </option>
                  </select>
                </div>

                <span class="hours-separator">bis</span>

                <div class="select hours-select">
                  <select v-model="activeDayData.closing_time" @change="emitUpdate">
                    <option :value="null">-- geschlossen --</option>
                    <option v-for="t in allTimeOptions" :key="'close-' + t.value" :value="t.value">
                      {{ t.name }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="hours-actions ml-auto" v-if="hasBaseOpeningHoursForDay">
                <button
                  type="button"
                  class="button is-light btn-copy-base-hours"
                  @click="copyFromBaseOpeningHours(activeDay)"
                  title="Öffnungszeiten aus den Stammdaten für diesen Tag übernehmen"
                >
                  <span class="material-symbols-outlined">sync</span>
                  Aus Stammdaten übernehmen
                </button>
              </div>
            </div>
          </div>

          <!-- PICKUP THRESHOLDS SECTION -->
          <div class="thresholds-section">
            <div class="thresholds-header">
              <div class="thresholds-title">
                <strong>Bestellfristen &amp; Abholzeiten (PickUp Thresholds)</strong><br>
                <span style="font-size: 12px;">
                  Definiert, wann vorbestellte Ware zur Abholung bereitsteht
                </span>
              </div>
              <div class="thresholds-count">
                <span class="tag is-info is-light is-small">
                  {{ activeDayData.pickUpThresholds.length }} Schwelle(n)
                </span>
              </div>
            </div>

            <!-- EMPTY THRESHOLDS STATE -->
            <div
              v-if="!activeDayData.pickUpThresholds || activeDayData.pickUpThresholds.length === 0"
              class="empty-thresholds"
            >
              <span class="material-symbols-outlined empty-state-icon">hourglass_empty</span>
              <p class="mb-2">Noch keine Abholzeiten für {{ currentDayMeta.label }} definiert.</p>
              <button
                type="button"
                class="button is-blue is-outline btn-add-threshold"
                @click="addThreshold"
              >
                <span class="material-symbols-outlined">add</span>
                Erste Abholzeit hinzufügen
              </button>
            </div>

            <!-- THRESHOLDS TABLE -->
            <div v-else class="table-container thresholds-table-wrap">
              <table class="table is-fullwidth is-striped is-narrow thresholds-table">
                <thead>
                  <tr>
                    <th style="width: 45px;">#</th>
                    <th style="min-width: 120px; width: 150px; max-width: 150px;">
                      Bestellung bis
                      <span class="hint-text"></span>
                    </th>
                    <th style="min-width: 120px; width: 150px; max-width: 150px;">
                      Abholbereit ab
                      <span class="hint-text"></span>
                    </th>
                    <th style="min-width: 160px;">
                      Folgetag
                    </th>
                    <th style="width: 90px; text-align: right;">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(threshold, index) in activeDayData.pickUpThresholds"
                    :key="index"
                    :class="{ 'is-null-threshold': !threshold.orderBeforeTime && !threshold.pickUpAtTime }"
                  >
                    <!-- INDEX -->
                    <td class="threshold-index-cell">
                      <span class="tag is-rounded is-light threshold-index">{{ index + 1 }}</span>
                    </td>

                    <!-- ORDER BEFORE -->
                    <td>
                      <div class="time-field-wrapper">
                        <input
                          type="time"
                          class="input time-picker-input"
                          v-model="threshold.orderBeforeTime"
                          @input="emitUpdate"
                          @change="emitUpdate"
                          placeholder="--:--"
                          title="Bestellzeit (orderBefore)"
                        />
                      </div>
                    </td>

                    <!-- PICKUP AT -->
                    <td>
                      <div class="time-field-wrapper">
                        <input
                          type="time"
                          class="input time-picker-input"
                          v-model="threshold.pickUpAtTime"
                          @input="emitUpdate"
                          @change="emitUpdate"
                          placeholder="--:--"
                          title="Abholbereit ab (pickUpAt)"
                        />
                      </div>
                    </td>

                    <!-- NEXT WORK DAY -->
                    <td>
                      <div class="next-day-controls">
                        <b-switch
                          :key="'nd-switch-' + activeDay + '-' + index + '-' + threshold.pickUpAtNextDay"
                          v-model="threshold.pickUpAtNextDay"
                          type="primary"
                          size="small"
                          @change="emitUpdate"
                          @input="emitUpdate"
                        ></b-switch>
                        <span
                          class="next-day-label"
                          :class="{ 'has-text-weight-bold has-text-link': threshold.pickUpAtNextDay }"
                          @click="threshold.pickUpAtNextDay = !threshold.pickUpAtNextDay; emitUpdate()"
                        >
                          nächster Werktag
                        </span>
                        <span
                          v-if="threshold.pickUpAtNextDay"
                          class="tag is-info is-light next-day-tag"
                        >
                          +1 Tag
                        </span>
                      </div>
                    </td>

                    <!-- ACTIONS -->
                    <td style="text-align: right;">
                      <div class="table-action-buttons">
                        <button
                          type="button"
                          class="button is-small is-light is-icon-only btn-table-action"
                          title="Schwelle duplizieren"
                          @click="duplicateThreshold(index)"
                        >
                          <span class="material-symbols-outlined">content_copy</span>
                        </button>
                        <button
                          type="button"
                          class="button is-small is-danger is-outline is-icon-only btn-table-action"
                          title="Schwelle entfernen"
                          @click="removeThreshold(index)"
                        >
                          <span class="material-symbols-outlined">delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- BUTTONS: ADD THRESHOLD -->
            <div class="threshold-add-actions">
              <button
                type="button"
                class="button is-blue is-outline btn-add-threshold"
                @click="addThreshold"
              >
                <span class="material-symbols-outlined">add</span>
                Abholzeit hinzufügen
              </button>

              <button
                type="button"
                class="button is-light btn-add-null"
                @click="addNullThreshold"
                title="Fügt eine leere Schwelle (orderBefore: null, pickUpAt: null) als Annahmeschluss hinzu"
              >
                <span class="material-symbols-outlined">block</span>
                Annahmeschluss (null)
              </button>

              <button
                v-if="activeDayData.pickUpThresholds.length > 1"
                type="button"
                class="button is-light btn-sort-thresholds"
                @click="sortThresholds"
                title="Schwellen chronologisch nach Bestellzeit sortieren"
              >
                <span class="material-symbols-outlined">sort</span>
                Nach Zeit sortieren
              </button>
            </div>
          </div>

          <!-- DAY BATCH ACTIONS / COPY TOOLS -->
          <div class="day-batch-actions">
            <div class="batch-title is-size-7 has-text-grey mb-2">
              <span class="material-symbols-outlined">bolt</span>
              <strong>Schnellfunktionen:</strong>
            </div>

            <div class="batch-buttons">
              <button
                type="button"
                class="button is-light btn-batch-action"
                @click="copyActiveDayToAllWeekdays"
                title="Überträgt alle Zeiten von diesem Tag auf Montag bis Freitag"
              >
                <span class="material-symbols-outlined">content_copy</span>
                Auf alle Werktage (Mo–Fr) übertragen
              </button>

              <div class="select copy-target-select">
                <select v-model="targetCopyDay" @change="onCopyTargetSelect">
                  <option :value="''">Auf einzelnen Tag kopieren...</option>
                  <option
                    v-for="d in weekDaysList.filter(d => d.key !== activeDay)"
                    :key="'copy-to-' + d.key"
                    :value="d.key"
                  >
                    Nach {{ d.label }} kopieren
                  </option>
                </select>
              </div>

              <button
                type="button"
                class="button is-light is-danger is-outline btn-clear-thresholds"
                @click="clearDayThresholds"
                v-if="activeDayData.pickUpThresholds.length > 0"
                title="Alle Schwellen für diesen Tag leeren"
              >
                <span class="material-symbols-outlined">delete_sweep</span>
                Schwellen leeren
              </button>
            </div>
          </div>
                </div>
      </div>

      <!-- FOOTER: RAW JSON PANEL -->
      <pharmacy-raw-json-panel
        :json-data="serializeAllDays()"
        title="JSON-Rohdaten (Öffnungs- & Abholzeiten)"
        button-label="JSON-Rohdaten anzeigen / importieren"
        apply-button-text="Aus JSON ins Formular übernehmen"
        success-message="JSON erfolgreich geladen und ins Formular übernommen!"
        :rows="14"
        placeholder="JSON hier einfügen..."
        @apply="onApplyRawJson"
      />
    </div>
  </div>
</template>

<script>
import PharmacyRawJsonPanel from './incl-pharmacy-raw-json-panel.vue';
const WEEKDAYS = [
  { key: 'Monday', label: 'Montag', short: 'Mo' },
  { key: 'Tuesday', label: 'Dienstag', short: 'Di' },
  { key: 'Wednesday', label: 'Mittwoch', short: 'Mi' },
  { key: 'Thursday', label: 'Donnerstag', short: 'Do' },
  { key: 'Friday', label: 'Freitag', short: 'Fr' },
  { key: 'Saturday', label: 'Samstag', short: 'Sa' },
  { key: 'Sunday', label: 'Sonntag', short: 'So' }
];

export default {
  name: 'OpenPickupTimes',

  components: {
    PharmacyRawJsonPanel
  },

  props: {
    // Expects object { Monday: { opening_time: '...', closing_time: '...', pickUpThresholds: [...] }, ... }
    // Or a JSON string that will be parsed.
    value: {
      type: [Object, String],
      default: () => ({})
    },

    // Optional base opening hours from site/config (value.openingHours)
    baseOpeningHours: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      activeDay: 'Monday',
      showAllDaysView: false,
      targetCopyDay: '',
      daysData: this.initDaysData(this.value),
      isInternalUpdate: false
    };
  },

  computed: {
    weekDaysList() {
      return WEEKDAYS;
    },

    currentDayMeta() {
      return WEEKDAYS.find(d => d.key === this.activeDay) || WEEKDAYS[0];
    },

    activeDayData() {
      if (!this.daysData[this.activeDay]) {
        this.$set(this.daysData, this.activeDay, {
          isOpen: true,
          opening_time: '08:00',
          closing_time: '18:00',
          pickUpThresholds: []
        });
      }
      return this.daysData[this.activeDay];
    },

    hasBaseOpeningHoursForDay() {
      return (
        this.baseOpeningHours &&
        this.baseOpeningHours[this.activeDay] &&
        (this.baseOpeningHours[this.activeDay].openingTime || this.baseOpeningHours[this.activeDay].closingTime)
      );
    },

    allTimeOptions() {
      const times = [];
      const quarterHours = ['00', '15', '30', '45'];

      for (let i = 0; i < 24; i++) {
        const hour = i < 10 ? '0' + i : '' + i;
        for (let j = 0; j < 4; j++) {
          const t = `${hour}:${quarterHours[j]}`;
          times.push({ name: t, value: t });
        }
      }

      // Collect any custom times present in the current daysData to ensure they appear in select
      const customTimes = new Set();
      Object.values(this.daysData).forEach(day => {
        if (day.opening_time) customTimes.add(day.opening_time);
        if (day.closing_time) customTimes.add(day.closing_time);
        (day.pickUpThresholds || []).forEach(t => {
          if (t.orderBeforeTime) customTimes.add(t.orderBeforeTime);
          if (t.pickUpAtTime) customTimes.add(t.pickUpAtTime);
        });
      });

      customTimes.forEach(ct => {
        if (ct && !times.some(item => item.value === ct)) {
          times.push({ name: ct, value: ct });
        }
      });

      times.sort((a, b) => a.value.localeCompare(b.value));
      return times;
    }
  },

  watch: {
    value: {
      handler(newVal) {
        if (this.isInternalUpdate) {
          this.isInternalUpdate = false;
          return;
        }
        this.daysData = this.initDaysData(newVal);
      },
      deep: true
    }
  },

  methods: {
    /**
     * Parse incoming value (Object or JSON String) into internal normalized daysData structure.
     */
    initDaysData(input) {
      let parsed = {};
      if (typeof input === 'string' && input.trim() !== '') {
        try {
          parsed = JSON.parse(input);
        } catch (e) {
          parsed = {};
        }
      } else if (input && typeof input === 'object') {
        parsed = input;
      }

      const result = {};

      WEEKDAYS.forEach(day => {
        const dayRaw = parsed[day.key];

        if (!dayRaw) {
          // Default: weekdays open 08:00 - 18:00, Saturday open 08:00 - 12:00, Sunday closed
          const isSunday = day.key === 'Sunday';
          const isSaturday = day.key === 'Saturday';
          result[day.key] = {
            isOpen: !isSunday,
            opening_time: isSunday ? null : '08:00',
            closing_time: isSunday ? null : isSaturday ? '12:00' : '18:00',
            pickUpThresholds: []
          };
          return;
        }

        // Determine if day is marked as open
        const hasHours = !!(dayRaw.opening_time || dayRaw.closing_time);
        const hasThresholds = Array.isArray(dayRaw.pickUpThresholds) && dayRaw.pickUpThresholds.length > 0;
        const isOpen = hasHours || hasThresholds;

        const thresholds = (dayRaw.pickUpThresholds || []).map(t => {
          return {
            orderBeforeTime: t && t.orderBefore ? t.orderBefore.time : null,
            orderBeforeNextDay: t && t.orderBefore ? !!t.orderBefore.onNextWorkDay : false,
            pickUpAtTime: t && t.pickUpAt ? t.pickUpAt.time : null,
            pickUpAtNextDay: t && t.pickUpAt ? !!t.pickUpAt.onNextWorkDay : false
          };
        });

        result[day.key] = {
          isOpen,
          opening_time: dayRaw.opening_time || null,
          closing_time: dayRaw.closing_time || null,
          pickUpThresholds: thresholds
        };
      });

      return result;
    },

    /**
     * Serialize internal daysData into the exact JSON Object format expected by server.
     */
    serializeAllDays() {
      const payload = {};

      WEEKDAYS.forEach(day => {
        const d = this.daysData[day.key];
        if (!d || !d.isOpen) {
          payload[day.key] = {
            opening_time: null,
            closing_time: null,
            pickUpThresholds: []
          };
          return;
        }

        const thresholds = (d.pickUpThresholds || []).map(t => {
          const hasOrderTime = t.orderBeforeTime !== null && t.orderBeforeTime !== '';
          const hasPickupTime = t.pickUpAtTime !== null && t.pickUpAtTime !== '';

          // Special case: both null (e.g. Annahmeschluss / empty row)
          if (!hasOrderTime && !hasPickupTime) {
            return {
              orderBefore: null,
              pickUpAt: null
            };
          }

          return {
            orderBefore: hasOrderTime
              ? {
                  time: t.orderBeforeTime,
                  onNextWorkDay: !!t.orderBeforeNextDay
                }
              : null,
            pickUpAt: hasPickupTime
              ? {
                  time: t.pickUpAtTime,
                  onNextWorkDay: !!t.pickUpAtNextDay
                }
              : null
          };
        });

        payload[day.key] = {
          opening_time: d.opening_time || null,
          closing_time: d.closing_time || null,
          pickUpThresholds: thresholds
        };
      });

      return payload;
    },

    /**
     * Emit updated payload to parent via v-model
     */
    emitUpdate() {
      const payload = this.serializeAllDays();
      this.isInternalUpdate = true;
      this.$emit('input', payload);
      this.$emit('change', payload);

      if (this.showRawJson) {
        this.rawJsonInput = JSON.stringify(payload, null, 2);
      }
    },

    onDayOpenToggle(val) {
      const day = this.activeDayData;
      if (typeof val === 'boolean') {
        this.$set(day, 'isOpen', val);
      }
      if (!day.isOpen) {
        day.opening_time = null;
        day.closing_time = null;
      } else {
        if (!day.opening_time) day.opening_time = '08:00';
        if (!day.closing_time) day.closing_time = this.activeDay === 'Saturday' ? '12:00' : '18:00';
      }
      this.emitUpdate();
    },

    openCurrentDay() {
      this.onDayOpenToggle(true);
    },

    addThreshold() {
      const day = this.activeDayData;
      if (!day.pickUpThresholds) {
        this.$set(day, 'pickUpThresholds', []);
      }

      // Default sensible suggestion based on existing thresholds
      let defaultOrder = '11:00';
      let defaultPickup = '14:30';
      let defaultNextDay = false;

      const len = day.pickUpThresholds.length;
      if (len === 1) {
        defaultOrder = '13:45';
        defaultPickup = '17:00';
      } else if (len === 2) {
        defaultOrder = '15:00';
        defaultPickup = '17:45';
      } else if (len === 3) {
        defaultOrder = '17:00';
        defaultPickup = '08:30';
        defaultNextDay = true;
      }

      day.pickUpThresholds.push({
        orderBeforeTime: defaultOrder,
        orderBeforeNextDay: false,
        pickUpAtTime: defaultPickup,
        pickUpAtNextDay: defaultNextDay
      });

      this.emitUpdate();
    },

    addNullThreshold() {
      const day = this.activeDayData;
      if (!day.pickUpThresholds) {
        this.$set(day, 'pickUpThresholds', []);
      }
      day.pickUpThresholds.push({
        orderBeforeTime: null,
        orderBeforeNextDay: false,
        pickUpAtTime: null,
        pickUpAtNextDay: false
      });
      this.emitUpdate();
    },

    duplicateThreshold(index) {
      const item = this.activeDayData.pickUpThresholds[index];
      if (!item) return;
      const clone = JSON.parse(JSON.stringify(item));
      this.activeDayData.pickUpThresholds.splice(index + 1, 0, clone);
      this.emitUpdate();
    },

    removeThreshold(index) {
      this.activeDayData.pickUpThresholds.splice(index, 1);
      this.emitUpdate();
    },

    sortThresholds() {
      const day = this.activeDayData;
      if (!day.pickUpThresholds) return;
      day.pickUpThresholds.sort((a, b) => {
        if (!a.orderBeforeTime) return 1;
        if (!b.orderBeforeTime) return -1;
        return a.orderBeforeTime.localeCompare(b.orderBeforeTime);
      });
      this.emitUpdate();
    },

    clearDayThresholds() {
      this.activeDayData.pickUpThresholds = [];
      this.emitUpdate();
    },

    copyFromBaseOpeningHours(dayKey) {
      if (!this.baseOpeningHours || !this.baseOpeningHours[dayKey]) return;
      const base = this.baseOpeningHours[dayKey];
      const day = this.daysData[dayKey];
      if (base.openingTime) day.opening_time = base.openingTime;
      if (base.closingTime) day.closing_time = base.closingTime;
      if (base.openingTime || base.closingTime) day.isOpen = true;
      this.emitUpdate();
    },

    copyActiveDayToAllWeekdays() {
      const source = this.activeDayData;
      const targetDays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

      targetDays.forEach(targetKey => {
        if (targetKey === this.activeDay) return;
        this.$set(this.daysData, targetKey, {
          isOpen: source.isOpen,
          opening_time: source.opening_time,
          closing_time: source.closing_time,
          pickUpThresholds: JSON.parse(JSON.stringify(source.pickUpThresholds || []))
        });
      });

      this.emitUpdate();
    },

    onCopyTargetSelect() {
      if (!this.targetCopyDay) return;
      const source = this.activeDayData;
      const targetKey = this.targetCopyDay;

      this.$set(this.daysData, targetKey, {
        isOpen: source.isOpen,
        opening_time: source.opening_time,
        closing_time: source.closing_time,
        pickUpThresholds: JSON.parse(JSON.stringify(source.pickUpThresholds || []))
      });

      this.targetCopyDay = '';
      this.emitUpdate();
    },

    onApplyRawJson(parsed, done) {
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return done('Das JSON muss ein Objekt mit Wochentagen als Schlüssel sein (z.B. { "Monday": { ... } }).');
      }
      this.daysData = this.initDaysData(parsed);
      this.emitUpdate();
      done();
    }
  }
};
</script>

<style lang="scss" scoped>
.open-pickup-times-control {
  margin-top: 14px;
  margin-bottom: 24px;
  align-items: flex-start;

  > .control-label {
    padding-top: 6px;
  }
}

.open-pickup-times-container {
  flex: 1;
  max-width: 950px;
  width: 100%;
}

/* DAY TABS HEADER */
.day-tabs-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e6e9ec;
}

.day-tabs-container {
  display: inline-flex;
  align-items: center;
  background-color: #eaedf1;
  border: 1px solid #d5dbe1;
  border-radius: 8px;
  padding: 3px;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.04);
}

.day-tabs {
  display: inline-flex;
  align-items: center;
  gap: 2px;
}

.day-tab-divider {
  width: 1px;
  height: 20px;
  background-color: #cbd5e1;
  margin: 0 4px;
}

.day-tab-button {
  height: 34px;
  padding: 0 12px;
  border: none;
  background: transparent;
  color: #495057;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  white-space: nowrap;
  user-select: none;
  transition: all 0.15s ease-in-out;
  outline: none;

  .day-label {
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
  }

  .day-badge {
    height: 18px;
    min-width: 18px;
    padding: 0 5px;
    font-size: 11px;
    font-weight: 700;
    line-height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;

    &.is-active-badge {
      background-color: #ffffff;
      color: #2780e3;
    }

    &.is-inactive-badge {
      background-color: #d8e2eb;
      color: #4a6177;
    }

    &.is-closed-tag {
      background-color: #d1d5db;
      color: #6b7280;
    }
  }

  .tab-icon {
    font-size: 17px;
    margin-right: 2px;
    display: inline-flex;
    align-items: center;
    line-height: 1;
  }

  &:hover:not(.is-active) {
    background-color: rgba(255, 255, 255, 0.7);
    color: #1a1a1a;

    .day-badge.is-inactive-badge {
      background-color: #c9d6e2;
    }
  }

  &.is-active {
    background-color: #2780e3;
    color: #ffffff;
    font-weight: 600;
    box-shadow: 0 2px 4px rgba(39, 128, 227, 0.35);

    .day-label {
      color: #ffffff;
    }

    .tab-icon {
      color: #ffffff;
    }
  }

  &.is-closed:not(.is-active) {
    opacity: 0.65;
  }
}

.button.is-icon-only {
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  text-align: center !important;

  .material-symbols-outlined,
  .icon,
  span {
    margin: 0 !important;
    margin-right: 0 !important;
    padding: 0 !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    line-height: 1 !important;
  }
}

/* ALL DAYS OVERVIEW */
.all-days-overview {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .overview-day-card {
    background-color: #f9f9f9;
    border: 1px solid #e5e5e5;
    border-radius: 6px;
    padding: 12px 16px;

    &.is-closed-card {
      background-color: #f2f2f2;
      opacity: 0.8;
    }

    .overview-day-header {
      display: flex;
      align-items: center;
      gap: 14px;

      .overview-day-title {
        display: inline-flex;
        align-items: center;
        gap: 12px;
        font-size: 16px;

        .day-status-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin: 0;
          font-size: 12px;
          height: 24px;
          padding: 0 10px;
          line-height: 1;
        }
      }

      .overview-day-hours {
        font-size: 13px;
      }

      .button {
        height: 34px;
        font-size: 13px;
        padding: 0 12px;
      }

      .material-symbols-outlined {
        font-size: 17px;
        margin-right: 4px;
      }
    }

    .overview-thresholds-chips {
      margin-top: 12px;
      display: flex;
      flex-wrap: wrap;
      gap: 8px 10px;

      .overview-threshold-chip {
        padding: 6px 12px;
        height: auto;
        font-size: 12px;
        line-height: 1.4;
        border-radius: 4px;
        display: inline-flex;
        align-items: center;

        .chip-next-day {
          margin-left: 6px;
        }

        &:last-child {
          margin-right: 0;
        }
      }
    }
  }
}

/* SINGLE DAY DETAIL CARD */
.day-detail-card {
  background-color: #f7f8f9;
  border: 1px solid #e2e5e8;
  border-radius: 6px;
  padding: 18px;
}

.day-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #e8ecef;
  padding-bottom: 14px;
  margin-bottom: 18px;

  .day-title-wrap {
    display: inline-flex;
    align-items: center;
    gap: 12px;

    .day-heading {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 20px;
      font-weight: 700;
      color: #333;
      line-height: 1;
      margin: 0;
      padding: 0;

      .material-symbols-outlined {
        font-size: 24px;
        color: #2780e3;
        display: inline-flex;
        align-items: center;
        line-height: 1;
        margin-right: 6px;
      }
    }

    .day-status-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin: 0;
      font-size: 12px;
      height: 24px;
      padding: 0 10px;
      line-height: 1;
    }
  }

  .day-status-toggle {
    display: inline-flex;
    align-items: center;
    gap: 12px;

    .status-toggle-label {
      font-size: 14px;
      font-weight: 600;
      color: #444;
      margin-bottom: 0;
    }

    ::v-deep .switch-wrap {
      margin-bottom: 0;
      margin-top: 0 !important;
      display: inline-flex;
      align-items: center;
    }

    ::v-deep .switchery {
      margin-top: 0 !important;
    }
  }
}

/* CLOSED DAY NOTICE */
.day-closed-notice {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background-color: #f0f2f4;
  border-radius: 4px;
  border: 1px dashed #c8d0d8;

  .closed-icon-wrap .material-symbols-outlined {
    font-size: 44px;
    color: #9aa0a6;
    margin-right: 14px;
  }

  .button {
    height: 36px;
    font-size: 13px;
    padding: 0 14px;

    .material-symbols-outlined {
      font-size: 18px;
      margin-right: 6px;
    }
  }
}

/* HOURS SECTION */
.hours-section {
  background-color: #fff;
  border: 1px solid #e6e9ec;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 20px;

  .hours-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;

    .hours-label {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;
      white-space: nowrap;

      .material-symbols-outlined {
        font-size: 20px;
        color: #2780e3;
        margin: 0;
      }
    }

    .hours-inputs {
      display: inline-flex;
      align-items: center;
      white-space: nowrap;

      .hours-prefix {
        font-size: 13px;
        color: #555;
        margin-right: 10px;
      }

      .select.hours-select {
        width: 100px;
        min-width: 100px;
        max-width: 100px;
        height: 36px;
        padding: 0;
        margin: 0;

        select {
          width: 100%;
          min-width: 100%;
          max-width: 100%;
          height: 36px;
          font-size: 13px;
          padding: 0 28px 0 10px;
          margin: 0;
        }

        &::after {
          right: 12px;
        }
      }

      .hours-separator {
        font-size: 13px;
        color: #555;
        margin: 0 12px;
        display: inline-block;
      }
    }

    .hours-actions {
      margin-left: auto;

      .btn-copy-base-hours {
        height: 36px;
        font-size: 12px;
        padding: 0 12px;
        white-space: nowrap;

        .material-symbols-outlined {
          font-size: 16px;
          margin-right: 4px;
        }
      }
    }
  }
}

.thresholds-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 14px;

    .thresholds-title {
      font-size: 15px;
    }

    .thresholds-count .tag {
      height: 26px;
      font-size: 12px;
      padding: 0 12px;
    }
  }

  .empty-thresholds {
    text-align: center;
    padding: 28px 16px;
    background-color: #fafbfc;
    border: 1px dashed #d5dbe0;
    border-radius: 4px;
    color: #777;

    .empty-state-icon {
      font-size: 36px;
      color: #bbb;
      margin-bottom: 8px;
      display: inline-block;
    }

    .button {
      height: 38px;
      font-size: 13px;
      padding: 0 16px;
      display: inline-flex;
      align-items: center;
      justify-content: center;

      .material-symbols-outlined {
        font-size: 18px;
        color: inherit;
        margin: 0 6px 0 0;
        display: inline-flex;
        align-items: center;
        line-height: 1;
      }
    }
  }

.thresholds-table-wrap {
  margin-bottom: 14px;
  overflow-x: auto;
}

.thresholds-table {
  font-size: 14px;
  margin-bottom: 0;

  th {
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #777;
    background-color: #f6f7f9;
    border-bottom: 2px solid #e3e7eb;
    padding: 10px 12px;

    .hint-text {
      font-weight: 400;
      text-transform: none;
      color: #aaa;
      margin-left: 4px;
    }
  }

  td {
    vertical-align: middle;
    padding: 10px 12px;
  }

  tr.is-null-threshold td {
    background-color: #fff9e6;
  }

  .threshold-index-cell {
    text-align: center;
    .threshold-index {
      font-weight: 700;
      font-size: 12px;
      height: 28px;
      min-width: 28px;
      line-height: 28px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
    }
  }

  .time-field-wrapper {
    display: inline-flex;
    align-items: center;

    .time-picker-input {
      width: 90px;
      height: 36px;
      font-size: 14px;
      padding: 0 10px;
      border-radius: 4px;
      box-shadow: inset 0 1px 2px rgba(10, 10, 10, 0.1);
      border-color: #dbdbdb;

      &:focus {
        border-color: #2780e3;
        box-shadow: 0 0 0 0.125em rgba(39, 128, 227, 0.25);
      }
    }
  }

  .next-day-controls {
    display: inline-flex;
    align-items: center;
    gap: 10px;

    ::v-deep .switch-wrap {
      margin-bottom: 0;
      display: inline-flex;
      align-items: center;
      cursor: pointer;
    }

    ::v-deep .switchery {
      margin-top: 0 !important;
    }

    .next-day-label {
      font-size: 13px;
      cursor: pointer;
      user-select: none;
      line-height: 1;
      color: #555;
      transition: color 0.15s ease;

      &:hover {
        color: #2780e3;
      }
    }

    .next-day-tag {
      height: 22px;
      font-size: 11px;
      font-weight: 600;
      padding: 0 8px;
      border-radius: 4px;
      line-height: 1;
      display: inline-flex;
      align-items: center;
      margin-left: 4px;
    }
  }

  .table-action-buttons {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;

    .button.is-icon-only,
    .btn-table-action {
      height: 28px !important;
      width: 28px !important;
      min-width: 28px !important;
      border-radius: 4px !important;
      padding: 0 !important;
      display: inline-flex !important;
      align-items: center !important;
      justify-content: center !important;
      margin: 0 0 0 4px !important;
      line-height: 1 !important;

      .material-symbols-outlined,
      .icon,
      span {
        margin: 0 !important;
        margin-right: 0 !important;
        padding: 0 !important;
        font-size: 15px !important;
        line-height: 1 !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        text-align: center !important;
      }
    }
  }
}

.threshold-add-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 12px;

  .button {
    height: 38px;
    font-size: 13px;
    padding: 0 16px;
    margin-right: 12px;
    margin-bottom: 6px;

    &.btn-sort-thresholds {
      margin-left: auto;
      margin-right: 0;
    }

    .material-symbols-outlined {
      font-size: 18px;
      margin-right: 6px;
    }
  }
}

/* DAY BATCH ACTIONS */
.day-batch-actions {
  background-color: #f1f4f6;
  border-radius: 6px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;

  .batch-title {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    line-height: 1;
    margin-bottom: 12px;

    .material-symbols-outlined {
      font-size: 18px;
      color: #f39c12;
      margin: 0;
      display: inline-flex;
      align-items: center;
      line-height: 1;
    }

    strong {
      display: inline-flex;
      align-items: center;
      line-height: 1;
    }
  }

  .batch-buttons {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;

    .button {
      height: 36px;
      font-size: 13px;
      padding: 0 14px;
      margin: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 1;

      &.btn-clear-thresholds {
        margin-left: auto;
      }

      .material-symbols-outlined {
        font-size: 17px;
        margin: 0 6px 0 0;
        display: inline-flex;
        align-items: center;
        line-height: 1;
      }
    }

    .copy-target-select {
      height: 36px;
      display: inline-flex;
      align-items: center;
      margin: 0;

      select {
        height: 36px;
        font-size: 13px;
        margin: 0;
        display: inline-flex;
        align-items: center;
        line-height: normal;
      }
    }
  }
}
</style>